import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Settings, Globe } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { SettingsDialog } from '@/components/setting/SettingsDialog'
import { LoginDialog } from '@/components/setting/LoginDialog'
import { useWalletStore } from '@/stores/useWalletStore'
import { useUIStore } from '@/stores/useUIStore'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import logo from '@/assets/images/logo.svg'
import iconHb from '@/assets/icons/icon-hb.svg'
import iconHb1 from '@/assets/icons/icon-hb-1.svg'

export function LayoutHeader() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile } = useBreakpoint()
  const { session, isLogin, logout } = useWalletStore()
  const { language, setLanguage, isNavToggle, setNavToggle } = useUIStore()

  const [showSettings, setShowSettings] = useState(false)
  const [showLogin, setShowLogin] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [userOpen, setUserOpen] = useState(false)

  const handleLangChange = (lang: string) => {
    setLanguage(lang)
    setLangOpen(false)
  }

  const handleLogout = async () => {
    setUserOpen(false)
    await logout()
    toast.success(t('toastTip.logoutSuccess'))
  }

  const handleAccountClick = () => {
    if (isLogin && session) {
      window.open('https://bloks.io/account/' + session.account.name)
    }
    setUserOpen(false)
  }

  const accountName = isLogin && session
    ? (session.account.shortName || session.account.name)
    : t('login.title')

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 h-[56px] md:h-[80px] bg-white shadow-[0_0.5px_2px_rgba(0,0,0,0.16)] flex items-center justify-between pr-5">
        {/* Left: toggle + logo */}
        <div className="flex items-center">
          <button
            onClick={() => setNavToggle(!isNavToggle)}
            className="pl-[14px] cursor-pointer"
          >
            <img
              src={isNavToggle ? iconHb : iconHb1}
              alt="menu"
              className="h-[30px] w-[30px]"
            />
          </button>

          {!isMobile && (
            <div
              className="ml-[38px] cursor-pointer"
              onClick={() => navigate({ to: '/' })}
            >
              <img src={logo} alt="Totoro" className="h-[42px] mt-[-2px] mr-3" />
            </div>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-4">
          {/* Settings */}
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center justify-center text-nav hover:text-nav-hover transition-colors cursor-pointer"
          >
            <Settings className="h-[22px] w-[22px]" />
          </button>

          {/* Language */}
          <Popover open={langOpen} onOpenChange={setLangOpen}>
            <PopoverTrigger
              className="flex items-center justify-center text-nav hover:text-nav-hover transition-colors cursor-pointer"
            >
              <Globe className="h-[22px] w-[22px]" />
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto min-w-[120px] p-0 border border-[#d1e2ce]">
              <div className="py-1">
                <button
                  onClick={() => handleLangChange('zh-CN')}
                  className={`w-full px-4 py-2 text-[14px] text-center cursor-pointer transition-colors border-b border-[#d1e2ce] ${language === 'zh-CN' ? 'text-[#498846] font-bold' : 'text-[#091b3d] hover:text-[#919da6]'}`}
                >
                  简体中文
                </button>
                <button
                  onClick={() => handleLangChange('en')}
                  className={`w-full px-4 py-2 text-[14px] text-center cursor-pointer transition-colors ${language === 'en' ? 'text-[#498846] font-bold' : 'text-[#091b3d] hover:text-[#919da6]'}`}
                >
                  EN
                </button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Wallet */}
          {isLogin ? (
            <Popover open={userOpen} onOpenChange={setUserOpen}>
              <PopoverTrigger
                className="block px-3 leading-[32px] bg-[#498846] shadow-[0_1px_0_#566f7e] rounded-full text-[14px] text-center text-white cursor-pointer whitespace-nowrap"
              >
                {accountName}
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto min-w-[120px] p-0 border border-[#d1e2ce]">
                <div className="py-1">
                  <button
                    onClick={handleAccountClick}
                    className="w-full px-4 py-2 text-[14px] text-center text-[#091b3d] cursor-pointer transition-colors border-b border-[#d1e2ce] hover:text-[#919da6]"
                  >
                    Account
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-[14px] text-center text-[#091b3d] cursor-pointer transition-colors hover:text-[#919da6]"
                  >
                    {t('public.logout')}
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <span
              onClick={() => setShowLogin(true)}
              className="block px-3 leading-[32px] bg-[#498846] shadow-[0_1px_0_#566f7e] rounded-full text-[14px] text-center text-white cursor-pointer whitespace-nowrap"
            >
              {accountName}
            </span>
          )}
        </div>
      </header>

      {/* Dialogs */}
      <SettingsDialog open={showSettings} onClose={() => setShowSettings(false)} />
      <LoginDialog open={showLogin} onClose={() => setShowLogin(false)} />
    </>
  )
}
